// A frame-time probe for measuring phone heat. DEV/E2E ONLY, off unless asked.
//
// The three phone-heat wins (see _paintBudget.js) are all claims about numbers
// nobody can see: how many device pixels the canvas is, and how many times a
// second it is redrawn. A screenshot cannot tell you either, and neither can
// the Performance panel on a phone that is plugged into nothing. So the page
// can be asked to report them about itself.
//
//   /explore#13/40.60/-74.05/LCc?perf=1     (the query goes BEFORE the hash)
//   /tide_stations/632?perf=1
//
// Two doors, and they are the same two `window.map` uses (see useMarineLayers
// for why the second one is build-time): `import.meta.env.DEV`, or a bundle
// built with `VITE_E2E_HANDLES=1`. In any other bundle `import.meta.env` is
// folded to a literal at `vite build` and every line below is unreachable.
// There is no production UI here and no way to turn one on.

const WINDOW_MS = 2000

const enabled = () => !!(import.meta.env?.DEV || import.meta.env?.VITE_E2E_HANDLES === '1')

// Frames per second over the trailing window, from a list of frame timestamps.
// Pure, and honest about a short sample: the divisor is how much of the window
// has actually been observed, not a flat 2 s, so the first reading after start
// is a real rate rather than a quarter of one.
export function ratePerSecond(stamps, now, windowMs = WINDOW_MS) {
  if (!stamps.length) return 0
  const oldest = stamps[0]
  const span = Math.min(now - oldest, windowMs)
  if (span <= 0) return 0
  return (stamps.length / span) * 1000
}

const trim = (stamps, now, windowMs = WINDOW_MS) => {
  while (stamps.length && now - stamps[0] > windowMs) stamps.shift()
  return stamps
}

export function startPerfProbe(win = typeof window === 'undefined' ? null : window) {
  if (!enabled() || !win?.document) return undefined
  let params
  try {
    params = new URLSearchParams(win.location.search)
  } catch {
    return undefined
  }
  if (params.get('perf') !== '1') return undefined

  const doc = win.document
  const frames = []
  const mapFrames = []
  // Monotonic, never trimmed — what `sample()` counts against.
  const totals = { frames: 0, mapFrames: 0 }
  let raf = 0
  let attachedMap = null
  const onMapRender = () => {
    mapFrames.push(win.performance.now())
    totals.mapFrames++
  }

  // The overlay is deliberately dumb: no React, no CSS file, nothing that could
  // exist in a bundle that does not reach this line.
  const box = doc.createElement('div')
  box.id = 'tnm-perf'
  box.setAttribute('aria-hidden', 'true')
  box.style.cssText = [
    'position:fixed',
    'left:8px',
    'bottom:8px',
    'z-index:99999',
    'padding:6px 8px',
    'border-radius:6px',
    'background:rgba(6,37,52,0.86)',
    'color:#eaf0f4',
    'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace',
    'white-space:pre',
    'pointer-events:none',
  ].join(';')
  doc.body?.appendChild(box)

  const readCanvas = () => {
    const canvas = doc.querySelector('canvas.maplibregl-canvas') || doc.querySelector('canvas')
    if (!canvas) return null
    const cssW = canvas.clientWidth || 0
    const cssH = canvas.clientHeight || 0
    return {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      canvasPixels: canvas.width * canvas.height,
      cssWidth: cssW,
      cssHeight: cssH,
      // What the map ACTUALLY rendered at, read off the canvas rather than
      // asked of the library: this is the number the DPR cap is claimed to
      // change, so it must not come from the same place that sets it.
      pixelRatio: cssW ? canvas.width / cssW : null,
    }
  }

  const handle = {
    windowMs: WINDOW_MS,
    fps: 0,
    mapFps: null,
    frames: 0,
    mapFrames: 0,
    devicePixelRatio: win.devicePixelRatio,
    canvasWidth: null,
    canvasHeight: null,
    canvasPixels: null,
    cssWidth: null,
    cssHeight: null,
    pixelRatio: null,
    hidden: !!doc.hidden,
    // Measure over an explicit window instead of reading the trailing one.
    // `mapFps` is the number that answers "did the repaint loop stop?" — the
    // page's own rAF keeps running whatever the map does, so a paused map shows
    // up here as mapFps ~0 against an unchanged fps.
    //
    // Counted off the monotonic totals, not off the trailing lists: those are
    // trimmed to WINDOW_MS as they grow, so a longer sample read from them
    // would silently undercount.
    sample(ms = WINDOW_MS) {
      const t0 = win.performance.now()
      const f0 = totals.frames
      const m0 = totals.mapFrames
      return new Promise((resolve) => {
        win.setTimeout(() => {
          const elapsed = win.performance.now() - t0
          resolve({
            ms: elapsed,
            fps: ((totals.frames - f0) / elapsed) * 1000,
            mapFps: ((totals.mapFrames - m0) / elapsed) * 1000,
            mapAttached: !!attachedMap,
            canvasPixels: handle.canvasPixels,
            pixelRatio: handle.pixelRatio,
            devicePixelRatio: win.devicePixelRatio,
            hidden: !!doc.hidden,
          })
        }, ms)
      })
    },
  }

  const tick = () => {
    const now = win.performance.now()
    frames.push(now)
    totals.frames++
    trim(frames, now)
    trim(mapFrames, now)

    // `window.map` appears when the map loads and is deleted when it unmounts,
    // so this is checked every frame rather than once.
    const map = win.map
    if (map !== attachedMap) {
      attachedMap?.off?.('render', onMapRender)
      attachedMap = map && typeof map.on === 'function' ? map : null
      attachedMap?.on?.('render', onMapRender)
      mapFrames.length = 0
    }

    handle.fps = ratePerSecond(frames, now)
    handle.frames = frames.length
    handle.mapFrames = mapFrames.length
    handle.mapFps = attachedMap ? ratePerSecond(mapFrames, now) : null
    handle.devicePixelRatio = win.devicePixelRatio
    handle.hidden = !!doc.hidden
    Object.assign(handle, readCanvas() || {})

    const px = handle.canvasPixels
    box.textContent = [
      `rAF   ${handle.fps.toFixed(1)}/s`,
      `map   ${handle.mapFps == null ? '--' : handle.mapFps.toFixed(1) + '/s'}`,
      `dpr   ${handle.pixelRatio == null ? '--' : handle.pixelRatio.toFixed(2)} (device ${win.devicePixelRatio})`,
      `canvas ${handle.canvasWidth ?? '--'}x${handle.canvasHeight ?? '--'} = ${px == null ? '--' : (px / 1e6).toFixed(2) + ' Mpx'}`,
    ].join('\n')

    raf = win.requestAnimationFrame(tick)
  }

  raf = win.requestAnimationFrame(tick)
  win.__tnmPerf = handle

  return () => {
    win.cancelAnimationFrame(raf)
    attachedMap?.off?.('render', onMapRender)
    box.remove()
    if (win.__tnmPerf === handle) delete win.__tnmPerf
  }
}
